import React, { useState, useEffect } from "react";
import LoadingSpinner from "../../Controls/Spinner/LoadingSpinner";
import moment from "moment";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import APIRequest from "../../../Services/commonAPIConsumer";
import "./ManageActivityLog.css";
import {
  excludePendingFullScreenRecords,
  filterStatisticsRows,
  isPendingFullScreenRecord,
} from "./openRecordFilters";

export default function ManageActivityLog({
  item,
  selectedLanguage,
  timeZoneAbbrevation,
  timeZoneRef,
  applyTimeZoneToDisplay,
  dateFormatValueUpper,
  DownloadActivityLog,
}) {
  const [selectedVersionNo, setSelectedVersionNo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openRecordExpanded, setOpenRecordExpanded] = useState({});
  const [sessionStats, setSessionStats] = useState({});
  const [activeSessionStatsId, setActiveSessionStatsId] = useState(null);
  const [searchPageStats, setSearchPageStats] = useState("");
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [versionSortDirection, setVersionSortDirection] = useState({});

  const getTimeFormat = (seconds) => {
    var time = "00:00:00";
    if (seconds != null) {
      var sec = parseInt(seconds, 10);
      var hours = Math.floor(sec / 3600);
      var minutes = Math.floor((sec - hours * 3600) / 60);
      sec = sec - hours * 3600 - minutes * 60;
      if (hours < 10) hours = "0" + hours;
      if (minutes < 10) minutes = "0" + minutes;
      if (sec < 10) sec = "0" + sec;
      time = hours + ":" + minutes + ":" + sec;
    }
    return time;
  };

  const resolveVersionNumber = (record) => {
    if (!record) return 1;
    const raw = record.versionNumber;
    const parsed = parseInt(String(raw).replace(/[^0-9]/g, ""), 10);
    return isNaN(parsed) || parsed <= 0 ? 1 : parsed;
  };

  const buildVersionRows = (records) => {
    if (!Array.isArray(records) || records.length === 0) {
      return [];
    }

    const latestByVersion = new Map();
    records.forEach((record) => {
      if (!record) return;
      const verNum = resolveVersionNumber(record);
      const existing = latestByVersion.get(verNum);
      if (!existing) {
        latestByVersion.set(verNum, { ...record });
        return;
      }
      const currentTime = new Date(record.modified || record.created || 0).getTime();
      const existingTime = new Date(existing.modified || existing.created || 0).getTime();
      if (currentTime > existingTime) {
        latestByVersion.set(verNum, { ...record });
      }
    });

    const versionRows = Array.from(latestByVersion.entries()).map(([verNum, item]) => {
      item.verNum = verNum;
      item.versionNumber = verNum;
      item.securityLevel = item.documentOptions?.distRestriction ? 3 : item.documentOptions?.readerEmail ? 2 : 1;
      let totalKB = 0;
      let validFound = false;
      if (item.inputFiles && item.inputFiles.length > 0) {
        item.inputFiles.forEach(file => {
          const sizeStr = file.fileSize?.toString().trim().toLowerCase();
          if (!sizeStr) return;
          const sizeValue = parseFloat(sizeStr);
          if (isNaN(sizeValue)) return;
          validFound = true;
          if (sizeStr.includes("mb")) totalKB += sizeValue * 1024;
          else if (sizeStr.includes("kb")) totalKB += sizeValue;
          else totalKB += sizeValue;
        });
      }
      item.originalSizeFormatted = validFound ? (totalKB >= 1024 ? `${Math.round(totalKB / 1024)} MB` : `${Math.round(totalKB)} KB`) : "0 KB";
      return item;
    });

    // Newest version on top.
    return versionRows.sort((a, b) => b.verNum - a.verNum);
  };

  const docVersions = buildVersionRows(item?.versionHistoryRecords);

  const selectedVersionModal =
    selectedVersionNo == null
      ? null
      : docVersions.find((v) => v.versionNumber === selectedVersionNo) || null;

  const handleOpenVersionModal = (version) => {
    setOpenRecordExpanded({});
    setActiveSessionStatsId(null);
    setVersionSortDirection({});
    setSelectedVersionNo(version?.versionNumber != null ? version.versionNumber : null);
  };

  const handleCloseVersionModal = () => {
    setOpenRecordExpanded({});
    setActiveSessionStatsId(null);
    setSelectedVersionNo(null);
    setVersionSortDirection({});
  };

  useEffect(() => {
    setSessionStats({});
    setActiveSessionStatsId(null);
    setOpenRecordExpanded({});
    setSelectedVersionNo(null);
    setVersionSortDirection({});
  }, [item?.documentId]);

  // Auto Lock and Restricted rows are not reader sessions, the manage tab reports 0 pages
  // and no duration for them. Mirrored here so both screens agree.
  const isStatsApplicableEvent = (openRecord) =>
    openRecord &&
    openRecord.event !== "Auto Lock" &&
    openRecord.event !== "Restricted" &&
    !isPendingFullScreenRecord(openRecord);

  // Same shape the manage tab stores on each record (GetActivityLogRowData), so the
  // expanded box can read totalPageViewed / timeFormatted the way the old screen does.
  const getSessionStats = (openRecord) => {
    if (!openRecord || !isStatsApplicableEvent(openRecord)) {
      return { pagesViewed: 0, duration: "00:00:00", data: [], loaded: false };
    }
    const stats = sessionStats[openRecord.id];
    const recDurationSec = openRecord.totalViewDuration != null ? openRecord.totalViewDuration : (openRecord.activityTime != null ? openRecord.activityTime : 0);
    const recPages = openRecord.totalPageViewed != null ? openRecord.totalPageViewed : (recDurationSec > 0 ? 1 : 0);
    const recDurationStr = getTimeFormat(recDurationSec);

    if (!stats || !stats.loaded) {
      return { pagesViewed: recPages, duration: recDurationStr, data: [], loaded: false };
    }

    const pagesViewed = stats.pagesViewed > 0 ? stats.pagesViewed : recPages;
    const duration = (stats.duration && stats.duration !== "00:00:00") ? stats.duration : recDurationStr;

    return {
      ...stats,
      pagesViewed,
      duration
    };
  };

  const fetchSessionPageStats = async (openRecordId) => {
    if (!openRecordId) return;

    setIsStatsLoading(true);
    try {
      const baseUrl = `${process.env.REACT_APP_API}${process.env.REACT_APP_API_GetStatisticsData}/${item.documentId}`;
      const params = new URLSearchParams();
      params.append("openRecordId", openRecordId);
      params.append("IsStats", "false");
      const url = `${baseUrl}?${params.toString()}`;

      const jsonDocs = await APIRequest.GET("", url);
      if (jsonDocs && jsonDocs === "Server maintenance") {
        return;
      }
      if (jsonDocs && jsonDocs.errorMessage) {
        console.error("Error fetching session page statistics:", jsonDocs.errorMessage);
        return;
      }

      const statsData = filterStatisticsRows(
        Array.isArray(jsonDocs) ? jsonDocs : [],
        item?.openRecords
      );
      const uniquePages = new Set(statsData.map(stat => stat.pageNumber));
      const totalPages = uniquePages.size;

      let totalSeconds = statsData.reduce((total, statItem) => {
        const hours = (statItem.totalHours || 0) * 3600;
        const minutes = (statItem.totalMinutes || 0) * 60;
        const seconds = statItem.totalSeconds || 0;
        return total + hours + minutes + seconds;
      }, 0);

      setSessionStats(prev => ({
        ...prev,
        [openRecordId]: {
          data: statsData,
          pagesViewed: totalPages,
          duration: getTimeFormat(totalSeconds),
          loaded: true
        }
      }));
    } catch (error) {
      console.error("Error fetching session page statistics:", error);
    } finally {
      setIsStatsLoading(false);
    }
  };

  const handleOpenRecordToggle = (openRecordId) => {
    const isExpanded = openRecordExpanded[openRecordId] || false;
    setOpenRecordExpanded(prev => ({
      ...prev,
      [openRecordId]: !isExpanded
    }));

    if (!isExpanded && openRecordId) {
      fetchSessionPageStats(openRecordId);
    }
  };

  const handleToggleTimeSort = (verId) => {
    setVersionSortDirection(prev => ({
      ...prev,
      [verId]: prev[verId] === "asc" ? "desc" : "asc"
    }));
  };

  const handleDownloadVersionActivityLog = async (version) => {
    if (!version) {
      if (DownloadActivityLog) DownloadActivityLog(item.documentId);
      return;
    }

    const verOpenRecords = getLogsForVersion(version) || [];

    const displayVerNo = version.versionNumber != null
      ? "V" + version.versionNumber
      : (version.versionNo ? "V" + version.versionNo : "V1");

    const hasReaderCol = version.securityLevel !== 1;
    const timeZoneStr = timeZoneAbbrevation?.current || "UTC";

    // Fetch page statistics for open records of this version
    let statsDocs = [];
    try {
      const openRecordIds = verOpenRecords.map(r => r.id).filter(Boolean).join(",");
      if (openRecordIds) {
        const baseUrl = `${process.env.REACT_APP_API}${process.env.REACT_APP_API_GetStatisticsData}/${item.documentId}`;
        const params = new URLSearchParams();
        params.append("openRecordId", openRecordIds);
        params.append("IsStats", "false");
        const url = `${baseUrl}?${params.toString()}`;
        const resp = await APIRequest.GET("", url);
        if (resp && Array.isArray(resp)) {
          statsDocs = filterStatisticsRows(resp, item?.openRecords);
        }
      }
    } catch (e) {
      console.error("Error fetching stats for CSV download:", e);
    }

    const docIdLabel = selectedLanguage?.LstLanguageCodes?.UI_DocumentID || "Document";
    const docNameLabel = selectedLanguage?.LstLanguageCodes?.UI_DocumentName || "Document";
    const downloadTimeLabel = selectedLanguage?.LstLanguageCodes?.UI_DownloadDateTimeUTC || "Download";

    // Same account level date pattern as the on screen rows, not a hard coded one.
    const currentFormattedTime = moment
      .utc(applyTimeZoneToDisplay(new Date(), timeZoneRef.current))
      .format(activityLogDateFormat());

    let csvContent = "";
    csvContent += `${docIdLabel},${item.documentId}\n`;
    csvContent += `${docNameLabel},${version.fileName || item.fileName || "Document"}\n`;
    csvContent += `${downloadTimeLabel},${currentFormattedTime}\n\n`;

    const eventHeader = selectedLanguage?.LstLanguageCodes?.UI_Event;
    const readerHeader = selectedLanguage?.LstLanguageCodes?.UI_Raeder;
    const timeHeader = (selectedLanguage?.LstLanguageCodes?.UI_TimeUTC || "Time (UTC)") + " (" + timeZoneStr + ")";
    const ipHeader = selectedLanguage?.LstLanguageCodes?.UI_IP;
    const locHeader = selectedLanguage?.LstLanguageCodes?.UI_Loaction;
    const countryHeader = selectedLanguage?.LstLanguageCodes?.UI_Country;
    const netHeader = selectedLanguage?.LstLanguageCodes?.UI_Network;
    const durationHeader = selectedLanguage?.LstLanguageCodes?.UI_ViewDuration;
    const pagesHeader = selectedLanguage?.LstLanguageCodes?.UI_PagesViewed;

    if (hasReaderCol) {
      csvContent += `${eventHeader},${readerHeader},${timeHeader},${ipHeader},${locHeader},${countryHeader},${netHeader},${durationHeader},${pagesHeader}\n`;
    } else {
      csvContent += `${eventHeader},${timeHeader},${ipHeader},${locHeader},${countryHeader},${netHeader},${durationHeader},${pagesHeader}\n`;
    }

    verOpenRecords.forEach((rec) => {
      const eventName = getEventName(rec.event);
      const reader = rec.email || "";
      const timeStr = rec.opened ? formatTime(rec.opened) : "";
      const ip = rec.ip || "";
      const location = rec.geolocation?.city && rec.geolocation?.region_code ? `"${rec.geolocation.city}, ${rec.geolocation.region_code}"` : "";
      const country = rec.geolocation?.country_name || "";
      const network = rec.geolocation?.org ? `"${rec.geolocation.org}"` : "";

      const recStats = statsDocs.filter(s => s.openRecordId === rec.id);

      let totalSec = 0;
      if (recStats.length > 0) {
        totalSec = recStats.reduce((acc, s) => acc + (s.totalHours || 0) * 3600 + (s.totalMinutes || 0) * 60 + (s.totalSeconds || 0), 0);
      }
      const durationStr = rec.timeFormatted || (totalSec > 0 ? getTimeFormat(totalSec) : "00:00:00");

      const uniquePages = new Set(recStats.map(s => s.pageNumber));
      const pagesViewed = rec.totalPageViewed != null ? rec.totalPageViewed : (uniquePages.size > 0 ? uniquePages.size : (recStats.length > 0 ? 1 : 0));

      if (hasReaderCol) {
        csvContent += `${eventName},${reader},${timeStr},${ip},${location},${country},${network},${durationStr},${pagesViewed}\n`;
      } else {
        csvContent += `${eventName},${timeStr},${ip},${location},${country},${network},${durationStr},${pagesViewed}\n`;
      }

      const pageNumLabel = selectedLanguage?.LstLanguageCodes?.UI_PageNumber || "Page Number";
      const timeStartLabel = selectedLanguage?.LstLanguageCodes?.UI_TimeStarted || "Time Started";
      const totalTimeViewedLabel = selectedLanguage?.LstLanguageCodes?.UI_TotalTimeViewed || "Total Time Viewed";

      csvContent += `${pageNumLabel},${timeStartLabel},${totalTimeViewedLabel}\n`;

      if (recStats.length > 0) {
        recStats.forEach((stat) => {
          const statTimeStr = stat.timeStarted ? formatTime(stat.timeStarted) : "";
          const statTotalTime = getTimeFormat((stat.totalHours || 0) * 3600 + (stat.totalMinutes || 0) * 60 + (stat.totalSeconds || 0));
          csvContent += `${stat.pageNumber},${statTimeStr},${statTotalTime}\n`;
        });
      }

      csvContent += `\n`;
    });

    const rawName = version.fileName || item.fileName || "Document";
    const docName = rawName.replace(/\.[^/.]+$/, "");
    const downloadFileName = `${docName}_ActivityLog_${displayVerNo}.csv`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", downloadFileName);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // applyTimeZoneToDisplay shifts the epoch by the configured offset, so the result has to
  // be read back with moment.utc. Using plain moment() applies the browser offset a second
  // time and the displayed time is wrong by the local offset. The date pattern comes from
  // the account settings (dateFormatValueUpper), it must not be hard coded.
  // dateFormatValueUpper is the moment pattern from the account DateFormat setting.
  // dateFormatValueOriginal is deliberately not used as a fallback, it keeps the raw
  // casing ("MM/dd/yyyy") which moment does not understand.
  const activityLogDateFormat = () => {
    const baseFormat = dateFormatValueUpper && dateFormatValueUpper !== "" ? dateFormatValueUpper : "MM/DD/YYYY";
    return `${baseFormat} HH:mm`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "-";
    const dateValue = new Date(timeStr);
    if (isNaN(dateValue.getTime())) return "-";
    return moment
      .utc(applyTimeZoneToDisplay(dateValue, timeZoneRef.current))
      .format(activityLogDateFormat());
  };

  const getEventName = (event) => {
    if (event == null) {
      return selectedLanguage?.LstLanguageCodes?.UI_opened;
    }
    if (event === "Auto Lock") {
      return selectedLanguage?.LstLanguageCodes?.UI_AutoLock;
    }
    return selectedLanguage?.LstLanguageCodes?.UI_Restricted;
  };

  const getLogsForVersion = (item) => {
    if (!item) return [];
    const targetVerNum = item.versionNumber;
    let rawRecords = item.openRecords;
    if (!rawRecords || rawRecords.length === 0) return [];

    // PendingFullScreen rows are placeholders for a session that never started, they are
    // not shown in the activity log (grid and CSV both read this list).
    const matchedLogs = excludePendingFullScreenRecords(rawRecords).filter(
      rec => resolveVersionNumber(rec) === targetVerNum
    );
    const versionId = item.versionNumber;
    const sortDir = (versionId != null && versionSortDirection[versionId]) || "desc";

    return [...matchedLogs].sort((a, b) => {
      const dateA = new Date(a.opened || a.activityTime || 0).getTime();
      const dateB = new Date(b.opened || b.activityTime || 0).getTime();
      return sortDir === "asc" ? dateA - dateB : dateB - dateA;
    });
  };
  const getFileSizeFormatted = (item) => {
    let totalKB = 0;
    let validFound = false;
    if (item?.inputFiles && item.inputFiles.length > 0) {
      item.inputFiles.forEach(file => {
        const sizeStr = file.fileSize?.toString().trim().toLowerCase();
        if (!sizeStr) return;
        const sizeValue = parseFloat(sizeStr);
        if (isNaN(sizeValue)) return;
        validFound = true;
        if (sizeStr.includes("mb")) totalKB += sizeValue * 1024;
        else if (sizeStr.includes("kb")) totalKB += sizeValue;
        else totalKB += sizeValue;
      });
    }
    if (validFound) {
      return totalKB >= 1024 ? `${Math.round(totalKB / 1024)} MB` : `${Math.round(totalKB)} KB`;
    }
    if (item?.originalSizeFormatted) return item.originalSizeFormatted;
    if (item?.fileSize) return item.fileSize;
    return "0 KB";
  };

  const getPagesCount = (item) => {
    return item?.pages || item?.pagesCount || item?.originalPagesCount || 0;
  };

  // Keyed by the real version number. docVersions is already de-duplicated per version
  // by buildVersionRows, so no row can silently overwrite another one here.
  const versionByNumMap = new Map();
  docVersions.forEach(item => {
    const vNum = resolveVersionNumber(item);
    versionByNumMap.set(vNum, item);
  });

  const versionSizePagesMap = new Map();
  const sortedVerNums = Array.from(versionByNumMap.keys()).sort((a, b) => a - b);

  sortedVerNums.forEach((vNum) => {
    const currentVer = versionByNumMap.get(vNum);
    const currentSize = getFileSizeFormatted(currentVer);
    const currentPages = getPagesCount(currentVer);
    const currentCombined = `${currentSize} / ${currentPages}`;

    let origDisplay = "0 KB / 0";
    let updatedDisplay = "N/A";

    if (vNum === 1 || !versionByNumMap.has(vNum - 1)) {
      origDisplay = currentCombined;
      updatedDisplay = "N/A";
    } else {
      const prevVer = versionByNumMap.get(vNum - 1);
      const prevSize = getFileSizeFormatted(prevVer);
      const prevPages = getPagesCount(prevVer);
      origDisplay = `${prevSize} / ${prevPages}`;
      updatedDisplay = currentCombined;
    }

    versionSizePagesMap.set(vNum, { origDisplay, updatedDisplay });
  });

  const versionsToRender = docVersions;

  return (
    <div className="pb-3 mb-1 w-100">
      {isStatsLoading ? <LoadingSpinner /> : null}
      {/* Main Version Overview Table Container */}
      <div className="innertable-responsive activityLogtable-container mb-2 w-100 border overflow-hidden" style={{ borderColor: "#cbd5e1", borderRadius: "0.5rem" }}>
        <table
          className="table mb-0 bg-white w-100"
          style={{ tableLayout: "fixed", width: "100%", borderCollapse: "collapse" }}
        >
          <thead className="bg-lightred">
            <tr>
              <th className="borderRight font-13 stats-th-ver">
                {selectedLanguage?.LstLanguageCodes?.UI_versionNo}
              </th>
              <th className="borderRight font-13 stats-th-orig">
                {selectedLanguage?.LstLanguageCodes?.UI_OriginalFileSize}/{selectedLanguage?.LstLanguageCodes?.UI_Pages}
              </th>
              <th className="borderRight font-13 stats-th-upd">
                {selectedLanguage?.LstLanguageCodes?.UI_UpdatedSize}/{selectedLanguage?.LstLanguageCodes?.UI_Pages}
              </th>
              <th className="borderRight font-13 stats-th-user">
                {selectedLanguage?.LstLanguageCodes?.UI_UpdatedBy}
              </th>
              <th className="borderRight font-13 stats-th-time">
                {(selectedLanguage?.LstLanguageCodes?.UI_UpdatedTime) + " (" + (timeZoneAbbrevation?.current) + ")"}
              </th>
              <th className="text-center font-13 stats-th-key">
                {selectedLanguage?.LstLanguageCodes?.UI_ReadersSnapshot}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading && docVersions.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center p-4">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : versionsToRender.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center p-4">
                  <div className="no-record mt-n1 d-flex align-items-center justify-content-center w-100">
                    <span className="material-icons me-1" style={{ color: '#ff9800' }}>report_problem</span>
                    <span className="font-13">{selectedLanguage?.LstLanguageCodes?.UI_NoRecordsFound || "No records found."}</span>
                  </div>
                </td>
              </tr>
            ) : (
              versionsToRender.map((version, idx) => {
                const verId = version?.id || version?.versionNumber || version?.versionNo;
                const displayVerNo = version.versionNumber != null
                  ? "V" + version.versionNumber
                  : (version.versionNo ? "V" + version.versionNo : "V" + (versionsToRender.length - idx));

                const vNumKey = version.verNum || version.versionNumber || 1;
                const sizeInfo = versionSizePagesMap.get(vNumKey) || {
                  origDisplay: `${getFileSizeFormatted(version)} / ${getPagesCount(version)}`,
                  updatedDisplay: (version.versionNumber === 1 || version.versionNo === "1" || version.versionNo === 1) ? "N/A" : `${getFileSizeFormatted(version)} / ${getPagesCount(version)}`
                };

                const updatedTimeFormatted = version.modified != null
                  ? formatTime(version.modified)
                  : formatTime(version.created);

                return (
                  <tr key={verId || idx} className="borderrow">
                    <td style={{ width: "10%", padding: "10px 16px" }} className="borderRight">
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleOpenVersionModal(version);
                        }}
                        className="ms-1"
                        style={{
                          color: "#3045c0fc",
                          cursor: "pointer"
                        }}
                      >
                        {displayVerNo}
                      </a>                    </td>
                    <td style={{ width: "18%", padding: "10px 12px" }} className="font-13 text-slate-700 borderRight">
                      {sizeInfo.origDisplay}
                    </td>
                    <td style={{ width: "18%", padding: "10px 12px" }} className="font-13 text-slate-700 borderRight">
                      {sizeInfo.updatedDisplay}
                    </td>
                    <td style={{ width: "24%", padding: "10px 12px" }} className="font-13 text-slate-700 borderRight">
                      {version.modifiedBy || version.createdBy}
                    </td>
                    <td style={{ width: "18%", padding: "10px 12px" }} className="font-13 text-slate-700 borderRight">
                      {updatedTimeFormatted}
                    </td>
                    <td style={{ width: "12%", padding: "10px 16px" }} className="text-center font-13">

                      <a style={{ color: '#3045c0fc', cursor: "pointer" }} className="ms-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenVersionModal(version);
                        }}>
                        {selectedLanguage.LstLanguageCodes.UI_ViewInfo}
                      </a>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Activity Log Version Modal Popup */}
      {selectedVersionModal && (() => {
        const verId = selectedVersionModal.versionNumber;
        const displayVerNo = "V" + selectedVersionModal.versionNumber;

        const verOpenRecords = getLogsForVersion(selectedVersionModal);
        const hasReaderCol = selectedVersionModal.securityLevel !== 1;

        return (
          <div
            className="modalCus documentversion-history shadow"
            id="activityLogModal"
            style={{
              display: "block",
              backgroundColor: "rgba(15, 23, 42, 0.5)",
              backdropFilter: "blur(4px)",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1040
            }}
          >
            <div
              className="modal-dialog modal-dialog-centered modal-xl"
              style={{ maxWidth: "90vw", width: "1100px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content border-0 shadow-lg bg-white" style={{ maxHeight: "88vh", display: "flex", flexDirection: "column", borderRadius: "0.5rem" }}>
                <div
                  className="p-3 d-flex align-items-center justify-content-between bg-lightred border-bottom" style={{ borderTopLeftRadius: "0.5rem", borderTopRightRadius: "0.5rem" }}
                >
                  <h4
                    title={selectedVersionModal.fileName || item.fileName}
                    className="mb-0 text-truncate font-16 fw-bold"
                    style={{ color: "#1e293b" }}
                  >
                    {selectedVersionModal.fileName || item.fileName} - {displayVerNo}
                  </h4>

                  <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    {DownloadActivityLog && (
                      <img
                        src="assets/img/icons/download.png"
                        className="h-30p"
                        style={{
                          height: "24px",
                          cursor: "pointer"
                        }}
                        alt="Download"
                        title={
                          selectedLanguage?.LstLanguageCodes?.UI_DownloadUserActivityLog
                        }
                        onClick={() => handleDownloadVersionActivityLog(selectedVersionModal)}
                      />
                    )}

                    <button
                      type="button"
                      className="btn modalclose download p-0 border-0 background-none"
                      onClick={handleCloseVersionModal}
                      style={{
                        background: "none",
                        color: "#ef4444",
                        cursor: "pointer"
                      }}
                    >
                      <span
                        className="material-icons"
                        style={{
                          fontSize: "24px",
                          color: "#d21513"
                        }}
                      >
                        highlight_off
                      </span>
                    </button>
                  </div>
                </div>
                {/* Modal Body Container */}
                <div className="p-4 custom-scrollbar" style={{ overflowY: "auto", flex: 1, backgroundColor: "#f8fafc" }}>
                  <div className="bg-white border shadow-sm p-3" style={{ borderRadius: "0.5rem" }}>
                    {/* Activity Log Table */}
                    <div className="innertable-responsive activityLogtable-container border overflow-hidden" style={{ borderColor: "#cbd5e1", borderRadius: "0.5rem" }}>
                      <table
                        className="table mb-0 bg-white w-100"
                        style={{ tableLayout: "fixed", width: "100%", borderCollapse: "collapse" }}
                      >
                        <thead className="bg-lightred">
                          <tr>
                            <th className="borderRight font-13" style={{ width: hasReaderCol ? "13%" : "15%", color: "#334155", fontWeight: "500", padding: "10px 12px" }}>
                              {selectedLanguage?.LstLanguageCodes?.UI_Event}
                            </th>
                            {hasReaderCol && (
                              <th className="borderRight font-13" style={{ width: "18%", color: "#334155", fontWeight: "500", padding: "10px 12px" }}>
                                {selectedLanguage?.LstLanguageCodes?.UI_Raeder}
                              </th>
                            )}
                            <th
                              className="borderRight font-13"
                              style={{ width: hasReaderCol ? "14%" : "15%", color: "#334155", fontWeight: "500", padding: "10px 12px", cursor: "pointer" }}
                              onClick={() => handleToggleTimeSort(verId)}
                            >
                              {(selectedLanguage?.LstLanguageCodes?.UI_TimeUTC) + " (" + (timeZoneAbbrevation?.current) + ") "}
                              <span className="font-11">{(versionSortDirection[verId] || "desc") === "asc" ? "▲" : "▼"}</span>
                            </th>
                            <th className="borderRight font-13" style={{ width: hasReaderCol ? "11%" : "14%", color: "#334155", fontWeight: "500", padding: "10px 12px" }}>
                              {selectedLanguage?.LstLanguageCodes?.UI_IP}
                            </th>
                            <th className="borderRight font-13" style={{ width: hasReaderCol ? "13%" : "16%", color: "#334155", fontWeight: "500", padding: "10px 12px" }}>
                              {selectedLanguage?.LstLanguageCodes?.UI_Loaction}
                            </th>
                            <th className="borderRight font-13" style={{ width: hasReaderCol ? "11%" : "15%", color: "#334155", fontWeight: "500", padding: "10px 12px" }}>
                              {selectedLanguage?.LstLanguageCodes?.UI_Country}
                            </th>
                            <th className="font-13" style={{ width: hasReaderCol ? "20%" : "25%", color: "#334155", fontWeight: "500", padding: "10px 12px" }}>
                              {selectedLanguage?.LstLanguageCodes?.UI_Network}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {verOpenRecords && verOpenRecords.length > 0 ? (
                            verOpenRecords.map((openRecord, index) => {
                              const isRecordOpen = openRecordExpanded[openRecord.id] || false;
                              const statsObj = getSessionStats(openRecord);

                              const readerVal = openRecord.email || "";
                              const timeVal = openRecord.opened != null ? formatTime(openRecord.opened) : "";
                              const ipVal = openRecord.ip || openRecord.ipAddress;

                              const city = openRecord.geolocation?.city || openRecord.city;
                              const region = openRecord.geolocation?.region_code || openRecord.geolocation?.region_name || openRecord.region_code || openRecord.region_name;
                              const locVal = city && region ? `${city}, ${region}` : (city || region || openRecord.location || openRecord.geolocation?.location);

                              const countryVal = openRecord.geolocation?.country_name || openRecord.geolocation?.country || openRecord.country_name || openRecord.country;

                              const netVal = openRecord.geolocation?.org || openRecord.geolocation?.network || openRecord.geolocation?.isp || openRecord.org || openRecord.network || openRecord.isp || openRecord.geolocation?.name || openRecord.name;

                              return (
                                <React.Fragment key={openRecord.id || index}>
                                  <tr
                                    className="borderrow"
                                    onClick={() => handleOpenRecordToggle(openRecord.id)}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <td style={{ width: hasReaderCol ? "13%" : "15%", padding: "10px 12px", whiteSpace: "nowrap" }} className="borderRight font-13 fw-semibold">
                                      <span
                                        className="material-icons me-1"
                                        style={{
                                          fontSize: "16px",
                                          color: "#000000",
                                          verticalAlign: "middle",
                                          display: "inline-block",
                                          transition: "transform 0.2s ease",
                                          transform: isRecordOpen ? "rotate(90deg)" : "rotate(0deg)"
                                        }}
                                      >
                                        play_arrow
                                      </span>
                                      {getEventName(openRecord.event)}
                                    </td>
                                    {hasReaderCol && (
                                      <td
                                        style={{ width: "18%", padding: "10px 12px" }}
                                        className="borderRight font-13 text-slate-700 text-truncate"
                                        title={readerVal}
                                      >
                                        {readerVal}
                                      </td>
                                    )}
                                    <td
                                      style={{ width: hasReaderCol ? "14%" : "15%", padding: "10px 12px" }}
                                      className="borderRight font-13 text-slate-700"
                                      title={timeVal !== "-" ? timeVal : ""}
                                    >
                                      {timeVal}
                                    </td>
                                    <td
                                      style={{ width: hasReaderCol ? "11%" : "14%", padding: "10px 12px" }}
                                      className="borderRight font-13 text-slate-700 text-truncate"
                                      title={ipVal !== "-" ? ipVal : ""}
                                    >
                                      {ipVal}
                                    </td>
                                    <td
                                      style={{ width: hasReaderCol ? "13%" : "16%", padding: "10px 12px" }}
                                      className="borderRight font-13 text-slate-700 text-truncate"
                                      title={locVal !== "-" ? locVal : ""}
                                    >
                                      {locVal}
                                    </td>
                                    <td
                                      style={{ width: hasReaderCol ? "11%" : "15%", padding: "10px 12px" }}
                                      className="borderRight font-13 text-slate-700 text-truncate"
                                      title={countryVal !== "-" ? countryVal : ""}
                                    >
                                      {countryVal}
                                    </td>
                                    <td
                                      style={{ width: hasReaderCol ? "20%" : "25%", padding: "10px 12px" }}
                                      className="font-13 text-slate-700 text-truncate"
                                      title={netVal !== "-" ? netVal : ""}
                                    >
                                      {netVal}
                                    </td>
                                  </tr>

                                  {/* Expanded Info Box for Selected Log Row matching original UI */}
                                  {isRecordOpen && (
                                    <tr className="fold open" key={`Openid-${openRecord.id}`}>
                                      <td colSpan={hasReaderCol ? 7 : 6} className="innertTableTd" style={{ padding: "0" }}>
                                        <div className="fold-content" style={{ padding: "0em", boxShadow: "none" }}>
                                          <div className="row mx-0 cardborder">
                                            <div className="bg-white mt-1" style={{ width: "45%" }}>
                                              <div className="row">
                                                <div className="col-md-6 col-sm-12 text-start d-flex align-items-center">
                                                  <span className="material-icons me-2" style={{ fontSize: '20px' }}>update</span>
                                                  <span className="font-13">
                                                    {selectedLanguage?.LstLanguageCodes?.UI_ViewDuration}:
                                                  </span>
                                                  <span className="ms-3 font-13" style={{ fontWeight: '600' }}>
                                                    {statsObj.duration}
                                                  </span>
                                                </div>
                                                <div className="col-md-6 col-sm-12 text-start border-left d-flex justify-content-end align-items-center">
                                                  <span className="font-13">
                                                    {selectedLanguage?.LstLanguageCodes?.UI_PagesViewed || "Pages Viewed"}:
                                                  </span>
                                                  <span className="ms-3 font-13" style={{ fontWeight: '600' }}>
                                                    {statsObj.pagesViewed}
                                                  </span>
                                                </div>
                                                <div className="d-flex align-items-center font-13 mt-2">
                                                  <span style={{ color: '#d21513' }} className="material-icons me-1">stacked_bar_chart</span>
                                                  <span>{selectedLanguage?.LstLanguageCodes?.UI_ReaderStatisticsbySession || "Reader Statistics by Session"}:</span>
                                                  <a
                                                    style={{ color: '#3045c0fc', cursor: "pointer" }}
                                                    className="ms-1"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setActiveSessionStatsId(openRecord.id);
                                                      setSearchPageStats("");
                                                    }}
                                                  >
                                                    {selectedLanguage?.LstLanguageCodes?.UI_View}
                                                  </a>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={hasReaderCol ? 7 : 6} className="text-center p-4">
                                <div className="no-record mt-n1 d-flex align-items-center justify-content-center w-100">
                                  <span className="material-icons me-1" style={{ color: '#ff9800' }}>report_problem</span>
                                  <span className="font-13">{selectedLanguage?.LstLanguageCodes?.UI_NoRecordsFound}</span>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="collapse show me-3 pb-2 pt-1 text-end font-12 text-muted">
                      {selectedLanguage?.LstLanguageCodes?.UI_LoactionRegisteredLoaction || "Location: registered location of the detected network"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Reader Statistics (This Session) Original Rectangular Box Modal Popup */}
      {activeSessionStatsId && (() => {
        const activeRecord = docVersions.flatMap(v => getLogsForVersion(v)).find(r => r.id === activeSessionStatsId);
        // Read through the same helper as the expanded box so an Auto Lock / Restricted
        // row does not show page statistics that do not belong to a reader session.
        const statsObj = getSessionStats(activeRecord);
        let statsList = statsObj.data || [];

        if (searchPageStats && statsList.length > 0) {
          const query = searchPageStats.trim().toLowerCase();
          statsList = statsList.filter(s => (s.pageNumber || "").toString().includes(query));
        }

        return (
          <div
            className="modalCus documentversion-history shadow"
            id="myModal"
            style={{
              display: "block",
              backgroundColor: "rgba(0,0,0,0.5)",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1050
            }}
          >
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "720px" }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-content border-0 shadow-lg bg-white" style={{ borderRadius: "0.5rem" }}>
                <div className="card-body p-3">
                  {/* Modal Header */}
                  <div
                    className="p-3 mb-3 justify-content-between d-flex align-items-center bg-lightred rounded-top-3"
                    style={{ borderBottom: "2px solid #cbd5e1" }}
                  >
                    <h4 className="mb-0 font-18 fw-bold" style={{ color: "#1e293b" }}>
                      {selectedLanguage?.LstLanguageCodes?.UI_PagesReadPerSession}
                    </h4>
                    <button
                      type="button"
                      className="btn modalclose download p-0 border-0 background-none"
                      onClick={() => setActiveSessionStatsId(null)}
                      style={{ background: "none", color: "#ef4444", cursor: "pointer" }}
                    >
                      <span className="material-icons" style={{ fontSize: "24px", color: "#d21513" }}>
                        highlight_off
                      </span>
                    </button>
                  </div>

                  {/* Metadata Header */}
                  <div className="card-body pt-0 px-0 pb-1" style={{ display: "block" }}>
                    <div className="d-flex justify-content-between align-items-center mb-1 font-13">
                      <div className="d-flex Docnamewrap">
                        <span className="fontWeight-500 fw-bold">{selectedLanguage?.LstLanguageCodes?.UI_DocumentName}:</span>
                        <span className="ms-1 text-slate-700">{item.fileName}</span>
                      </div>
                      <div className="d-flex">
                        <span className="fontWeight-500 fw-bold">{selectedLanguage?.LstLanguageCodes?.UI_DocumentID}:</span>
                        <span className="ms-1 text-slate-700">{item.documentId}</span>
                      </div>
                    </div>
                    <div className="d-flex mb-3 font-13">
                      <span className="fontWeight-500 fw-bold">{selectedLanguage?.LstLanguageCodes?.UI_ReaderEmail}:</span>
                      <span className="ms-1 text-slate-700">{activeRecord?.email}</span>
                    </div>

                    {/* Reader Statistics by Session Table Container */}
                    <div className="track-container mt-2">
                      <div className="reader-statis d-flex justify-content-between align-items-center">
                        <div style={{ color: '#d21513' }} className="d-flex align-items-center ms-2">
                          <span className="material-icons me-1">stacked_bar_chart</span>
                          <span style={{ color: '#272d4efc' }}>
                            {selectedLanguage?.LstLanguageCodes?.UI_ReaderStatisticsbySession}
                          </span>
                        </div>
                        <div className="position-relative number-search" style={{ maxWidth: "150px", flexShrink: 0, marginRight: '12px' }}>
                          <input
                            type="search"
                            className="form-control custom-inputBorder"
                            placeholder={`${selectedLanguage?.LstLanguageCodes?.UI_PageNumber}...`}
                            value={searchPageStats}
                            onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ""))}
                            onChange={(e) => setSearchPageStats(e.target.value)}
                          />
                        </div>
                      </div>

                      {/* Header Row */}
                      <div className="d-flex border py-2 track-header pe-4">
                        <div className="flex-fill text-center w-25 text-wrap font-13 firefox-22">
                          {selectedLanguage?.LstLanguageCodes?.UI_PageNumber}
                        </div>
                        <div className="flex-fill text-center w-50 px-2 text-wrap font-13">
                          {selectedLanguage?.LstLanguageCodes?.UI_TimeStarted}
                        </div>
                        <div className="flex-fill text-center w-25 px-2 text-wrap font-13">
                          {selectedLanguage?.LstLanguageCodes?.UI_TotalTimeViewed}
                        </div>
                      </div>

                      {/* Data Body */}
                      <div className={`track-bodyscroll ${statsList.length > 0 ? "" : "track-noBodyscroll"}`} style={{ maxHeight: "250px", overflowY: "auto" }}>
                        {isStatsLoading ? (
                          <div className="d-flex py-4 align-items-center justify-content-center text-muted font-13">
                            <LoadingSpinner class="backgroundTransprent" />
                          </div>
                        ) : statsList.length > 0 ? (
                          statsList.map((openstatistics, l) => (
                            <div className="d-flex border-bottom py-2 align-items-center borderrow pe-3 font-13 text-slate-700" key={l}>
                              <div className="flex-fill text-center w-25 text-wrap font-13">
                                {openstatistics.pageNumber}
                              </div>
                              <div className="flex-fill text-center w-50 px-2 text-wrap font-13">
                                {formatTime(openstatistics.timeStarted)}
                              </div>
                              <div className="flex-fill text-center pcs w-25 px-2 text-wrap font-13">
                                {getTimeFormat((openstatistics.totalHours || 0) * 3600 + (openstatistics.totalMinutes || 0) * 60 + (openstatistics.totalSeconds || 0))}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="d-flex py-3 align-items-center borderrow justify-content-center">
                            <span className="material-icons" style={{ fontSize: "18px", color: '#ff9800' }}>report_problem</span>
                            <span className="font-13 ms-2" style={{ fontWeight: '600' }}>{selectedLanguage?.LstLanguageCodes?.UI_NoRecordsFound}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
